import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { TasksService } from '../Project/Pages/Tasks_new/services/TasksService'
import { useTasksStore } from '../Project/Store/TasksStore'
import { useModalStore } from '../Project/Store/ModalStore'
import { Task } from '../Project/Pages/Tasks_new/types'

export const FetchSponsorsTasks = () => {
	const tasks = useTasksStore(state => state.tasks)
	const setTasks = useTasksStore(state => state.setTasks)
	const setIsLoading = useTasksStore(state => state.setIsLoading)
	const setRefreshSponsorsTasks = useTasksStore(state => state.setRefreshSponsorsTasks)
	const setLastSponsorsFetchTime = useTasksStore(state => state.setLastSponsorsFetchTime)
	const canRefreshSponsors = useTasksStore(state => state.canRefreshSponsors)
	const needInitialFetch = useTasksStore(state => state.needInitialFetch)
	const setNeedInitialFetch = useTasksStore(state => state.setNeedInitialFetch)
	const modalOpen = useModalStore(state => state.open)
	const isFirstLoad = useRef(true)
	const location = useLocation()
	const intervalRef = useRef<NodeJS.Timeout | null>(null)
	const wasOnSponsorsPageRef = useRef<boolean>(false)
	const initialPageRef = useRef<string | null>(null)

	const fetchTasks = async () => {
		try {
			console.log('🔄 SponsorsTaskFetch - выполняется fetchTasks')
			setIsLoading(true)
			const data = await TasksService.getTasks('sponsors', 3)

			// Получаем текущие задания из стора
			const currentTasks = useTasksStore.getState().tasks

			// Если заданий нет, просто устанавливаем новые
			if (currentTasks.length === 0) {
				setTasks(data)
			} else {
				// Создаем Map для быстрого поиска существующих заданий
				const existingTasksMap = new Map(currentTasks.map(task => [task.id, task]))

				// Обновляем существующие задания и добавляем новые
				const updatedTasks = data.map(newTask => {
					const existingTask = existingTasksMap.get(newTask.id)
					if (existingTask) {
						// Обновляем существующее задание, сохраняя локальные изменения
						return {
							...existingTask,
							...newTask,
							// Сохраняем локальный статус, если он более "продвинутый"
							status: getMoreAdvancedStatus(existingTask.status, newTask.status),
						}
					}
					return newTask
				})

				setTasks(updatedTasks)
				console.log('🔄 SponsorsTaskFetch - обновлены задания с новыми статусами')
			}

			setIsLoading(false)
			setLastSponsorsFetchTime(Date.now())

			const availableTasks = data.filter(task => task.status === 'NOT_STARTED')

			if (availableTasks.length > 0) {
				if (isFirstLoad.current) {
					isFirstLoad.current = false
				}
			}
		} catch (error) {
			console.error('Ошибка при getFreezedTasks:', error)
			setIsLoading(false)
		}
	}

	// Функция для определения более "продвинутого" статуса
	const getMoreAdvancedStatus = (localStatus: string, serverStatus: string): string => {
		const statusPriority: Record<string, number> = {
			NOT_STARTED: 1,
			IN_PROGRESS: 2,
			WAITING: 3,
			DONE: 4,
			CLAIMED: 5,
		}

		const localPriority = statusPriority[localStatus] || 0
		const serverPriority = statusPriority[serverStatus] || 0

		// Возвращаем статус с более высоким приоритетом
		return localPriority >= serverPriority ? localStatus : serverStatus
	}

	const forceRefresh = async () => {
		console.log('🔄 Принудительное обновление спонсорских заданий')
		if (intervalRef.current) {
			clearTimeout(intervalRef.current)
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}

		try {
			console.log('🔄 SponsorsTaskFetch - выполняется принудительное обновление')
			setIsLoading(true)
			const data = await TasksService.getTasks('sponsors', 3)

			// Получаем текущие задания из стора
			const currentTasks = useTasksStore.getState().tasks

			// Если заданий нет, просто устанавливаем новые
			if (currentTasks.length === 0) {
				setTasks(data)
			} else {
				// Создаем Map для быстрого поиска существующих заданий
				const existingTasksMap = new Map(currentTasks.map(task => [task.id, task]))

				// Обновляем существующие задания и добавляем новые
				const updatedTasks = data.map(newTask => {
					const existingTask = existingTasksMap.get(newTask.id)
					if (existingTask) {
						// Обновляем существующее задание, сохраняя локальные изменения
						return {
							...existingTask,
							...newTask,
							// Сохраняем локальный статус, если он более "продвинутый"
							status: getMoreAdvancedStatus(existingTask.status, newTask.status),
						}
					}
					return newTask
				})

				setTasks(updatedTasks)
				console.log('🔄 SponsorsTaskFetch - принудительно обновлены задания с новыми статусами')
			}

			setIsLoading(false)
			setLastSponsorsFetchTime(Date.now())

			const availableTasks = data.filter(task => task.status === 'NOT_STARTED')

			if (availableTasks.length > 0) {
				if (isFirstLoad.current) {
					isFirstLoad.current = false
				}
			}
		} catch (error) {
			console.error('Ошибка при принудительном обновлении:', error)
			setIsLoading(false)
		}
	}

	const startAutoFetch = () => {
		console.log('🚀 SponsorsTaskFetch - запуск startAutoFetch')

		if (intervalRef.current) {
			console.log('🧹 Очищаем предыдущий интервал')
			clearTimeout(intervalRef.current)
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}

		// Проверяем, можно ли делать обновление при запуске автофетча
		if (canRefreshSponsors()) {
			console.log('🔄 Выполняем немедленный fetch при запуске автофетча')
			fetchTasks()
		} else {
			console.log('⏳ Пропускаем немедленный fetch - таймер еще не истек')
		}

		// Вычисляем оставшееся время до следующего обновления
		const timeSinceLastFetch = Date.now() - useTasksStore.getState().lastSponsorsFetchTime
		const remainingTime = Math.max(0, 60000 - timeSinceLastFetch)

		console.log(`⏰ Устанавливаем интервал через ${Math.round(remainingTime / 1000)}с`)

		// Устанавливаем интервал с учетом оставшегося времени
		intervalRef.current = setTimeout(() => {
			console.log('⏰ SponsorsTaskFetch - выполнение по интервалу')
			fetchTasks()

			// После первого выполнения устанавливаем регулярный интервал
			intervalRef.current = setInterval(() => {
				console.log('⏰ SponsorsTaskFetch - выполнение по регулярному интервалу')
				fetchTasks()
			}, 60000)
		}, remainingTime)

		console.log('⏰ SponsorsTaskFetch - интервал установлен, ID:', intervalRef.current)
	}

	useEffect(() => {
		const currentPath = location.pathname
		const isOnSponsorsPage = currentPath === '/tasks/sponsors'

		console.log(
			'📍 SponsorsTaskFetch - текущая страница:',
			currentPath,
			'На странице спонсоров:',
			isOnSponsorsPage,
			'Был на странице спонсоров:',
			wasOnSponsorsPageRef.current,
			'Активный интервал:',
			!!intervalRef.current
		)

		// Если мы на странице спонсоров
		if (isOnSponsorsPage) {
			// Останавливаем автофетч
			if (intervalRef.current) {
				console.log('⏸️ Останавливаем автофетч на странице спонсоров')
				clearTimeout(intervalRef.current)
				clearInterval(intervalRef.current)
				intervalRef.current = null
			}

			// Проверяем, была ли начальная загрузка на странице спонсоров
			const wasInitialLoadOnSponsors = initialPageRef.current === '/tasks/sponsors'

			if (!wasOnSponsorsPageRef.current && !wasInitialLoadOnSponsors) {
				// Переход на страницу спонсоров с другой страницы - используем данные из стора
				console.log('📋 Переход на страницу спонсоров - используем данные из стора')
			} else if (!wasOnSponsorsPageRef.current && wasInitialLoadOnSponsors && needInitialFetch) {
				// Перезагрузка страницы на странице спонсоров - делаем fetch только если флаг установлен
				console.log('🔄 Перезагрузка на странице спонсоров, выполняем fetch')
				fetchTasks()
				setNeedInitialFetch(false) // Сбрасываем флаг после fetch
			} else {
				console.log('⏭️ Пропускаем fetch - уже загружено или не нужно')
			}

			wasOnSponsorsPageRef.current = true
			return
		}

		// Если мы перешли с страницы спонсоров на другую страницу, запускаем автофетч
		if (wasOnSponsorsPageRef.current && !intervalRef.current) {
			console.log('🔄 Переход с страницы спонсоров, перезапускаем автофетч')
			startAutoFetch()
		}

		wasOnSponsorsPageRef.current = false
	}, [location.pathname])

	// Запускаем автофетч при первом рендере
	useEffect(() => {
		const currentPath = location.pathname
		const isOnSponsorsPage = currentPath === '/tasks/sponsors'

		console.log('🎬 SponsorsTaskFetch - первый рендер, страница:', currentPath)

		// Запоминаем начальную страницу
		initialPageRef.current = currentPath

		// Всегда устанавливаем колбэк для обновления, независимо от страницы
		setRefreshSponsorsTasks(forceRefresh)

		if (!isOnSponsorsPage) {
			startAutoFetch()
		} else {
			// Если первая загрузка на странице спонсоров, устанавливаем флаг и делаем fetch
			console.log('🔄 Первая загрузка на странице спонсоров, устанавливаем флаг и выполняем fetch')
			setNeedInitialFetch(true)
			fetchTasks()
			wasOnSponsorsPageRef.current = true
		}

		return () => {
			if (intervalRef.current) {
				console.log('🧹 SponsorsTaskFetch - очистка интервала при размонтировании')
				clearTimeout(intervalRef.current)
				clearInterval(intervalRef.current)
				intervalRef.current = null
			}
		}
	}, [])

	return null
}
